import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Star, Gift, Sparkles, ChevronRight } from "lucide-react";

const STORAGE_KEY = "klrn_rewards_dismissed";
const MEMBER_KEY = "portal_token";
const VISIT_KEY = "klrn_visit_count";
const SHOW_DELAY = 4000;

export default function RewardsPrompt() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    // Don't show if already a member
    if (localStorage.getItem(MEMBER_KEY)) return;

    // Don't show on homepage (the WelcomeRewardsPopup handles that)
    if (window.location.pathname === "/" || window.location.pathname === "") return;

    // Track visits
    const visits = parseInt(localStorage.getItem(VISIT_KEY) || "0") + 1;
    localStorage.setItem(VISIT_KEY, visits.toString());

    // Check if dismissed recently (24 hours)
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed);
      if (Date.now() - dismissedAt < 24 * 60 * 60 * 1000) return;
    }

    // Show after delay on 2nd+ visit
    if (visits < 2) return;
    const timer = setTimeout(() => {
      setShow(true);
      setTimeout(() => setAnimate(true), 50);
    }, SHOW_DELAY);

    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setAnimate(false);
    setTimeout(() => {
      setShow(false);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    }, 300);
  };

  const goSignup = () => {
    dismiss();
    navigate("/my-account");
  };

  if (!show) return null;

  return (
    <div className={`fixed bottom-4 right-4 left-4 md:left-auto md:w-96 z-[9999] transition-all duration-500 ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-amber-900/40 md:shadow-amber-900/30"
        style={{ 
          background: "linear-gradient(135deg, #1f1a14 0%, #2d261c 50%, #1f1a14 100%)", 
          border: "2px solid rgba(212,175,55,0.7)" 
        }}>
        {/* Glow effect - stronger */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse at top right, rgba(212,175,55,0.25) 0%, transparent 60%)"
        }} />

        <button onClick={dismiss} className="absolute top-3 right-3 text-zinc-400 hover:text-white z-10 bg-zinc-700/60 rounded-full p-1" data-testid="rewards-prompt-close">
          <X className="w-4 h-4" />
        </button>

        <div className="p-5 relative">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-lg"
              style={{ background: "linear-gradient(135deg, #D4AF37, #c9a030)" }}>
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white text-base font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Join Crown Reserve
              </h3>
              <p className="text-zinc-200 text-sm leading-relaxed">
                Earn points on every visit. Get exclusive discounts, birthday rewards & VIP perks.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-1.5 text-amber-300 text-xs font-medium">
              <Star className="w-3.5 h-3.5" /> 1,000 pts signup bonus
            </div>
            <div className="flex items-center gap-1.5 text-amber-300 text-xs font-medium">
              <Gift className="w-3.5 h-3.5" /> Birthday rewards
            </div>
          </div>

          <button onClick={goSignup} data-testid="rewards-prompt-signup"
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-black transition-all hover:brightness-110 shadow-lg"
            style={{ background: "linear-gradient(135deg, #D4AF37, #e6c349)" }}>
            Sign Up Free <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
